import {
  Col,
  DatePicker,
  Form,
  Row,
  Table,
  Tag,
  Button,
  Input,
  Popover,
  Card,
  Typography,
  Grid,
} from "antd";
import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import KCA_Service from "../../../../services/KCA-Service/KCA_Service";
import ResponseMessage from "../../../Comman/Notification/ResponseMessge";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { pdf } from "@react-pdf/renderer";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const { Text: AntText } = Typography;
const { useBreakpoint } = Grid;

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
    backgroundColor: "#fff",
  },
  tags: {
    flexDirection: "row",
    marginBottom: 10,
    gap: 10,
  },
  tag: {
    padding: 4,
    backgroundColor: "#f0f0f0",
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
    fontSize: 8,
  },
  header: {
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#f6f6f6",
    padding: 10,
    borderRadius: 4,
  },
  headerText: {
    fontSize: 12,
    fontWeight: "bold",
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#bfbfbf",
  },
  tableHeader: {
    backgroundColor: "#f0f0f0",
  },
  tableCell: {
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: "#bfbfbf",
    fontSize: 8,
    textAlign: "center",
  },
  sizesContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
});

const pdfColumns = [
  { header: "SI. No", key: "sno", width: "5%" },
  { header: "GSM ID", key: "gsm_m_id", width: "10%" },
  { header: "Kanban No", key: "kanban_no", width: "10%" },
  { header: "Size & Qty", key: "size_qty", width: "15%" },
  { header: "Total Qty", key: "total_qty", width: "10%" },
  { header: "Kanban Status", key: "kanban_status", width: "10%" },
  { header: "Added By", key: "added_by", width: "10%" },
  { header: "Added On", key: "added_on", width: "15%" },
  { header: "Cleared By", key: "cleared_by", width: "10%" },
  { header: "Cleared On", key: "cleared_on", width: "15%" },
];

const DownloadPdf = ({ reportData, startDate, endDate }) => {
  const formatSize = (sizeStr) => {
    try {
      return Object.entries(JSON.parse(sizeStr));
    } catch {
      return [];
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toISOString().replace("T", " ").substring(0, 19);
    } catch {
      return dateStr;
    }
  };

  const formatHeaderDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getTotalQuantity = () =>
    reportData.reduce((sum, row) => sum + parseInt(row.total_qty || 0, 10), 0);

  const rowsPerPage = 13;
  const totalPages = Math.ceil(reportData.length / rowsPerPage);
  const pageSize = [842, 600];

  return (
    <Document>
      {[...Array(totalPages)].map((_, pageIndex) => (
        <Page key={pageIndex} size={pageSize} style={styles.page}>
          <View style={styles.header}>
            <Text style={styles.headerText}>Daily Report</Text>
          </View>

          <View style={styles.tags}>
            <View style={styles.tag}>
              <Text>Total Records: {reportData.length}</Text>
            </View>
            <View style={styles.tag}>
              <Text>Total Quantity: {getTotalQuantity()}</Text>
            </View>
            <View style={styles.tag}>
              <Text>
                Period: {formatHeaderDate(startDate)} - {formatHeaderDate(endDate)}
              </Text>
            </View>
          </View>

          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]} fixed>
              {pdfColumns.map((column) => (
                <View
                  key={column.key}
                  style={[styles.tableCell, { width: column.width }]}
                >
                  <Text>{column.header}</Text>
                </View>
              ))}
            </View>

            {reportData
              .slice(pageIndex * rowsPerPage, (pageIndex + 1) * rowsPerPage)
              .map((row, index) => (
                <View key={row.gsm_m_id || index} style={styles.tableRow}>
                  {pdfColumns.map((column) => (
                    <View
                      key={column.key}
                      style={[styles.tableCell, { width: column.width }]}
                    >
                      {column.key === "sno" ? (
                        <Text>{pageIndex * rowsPerPage + index + 1}</Text>
                      ) : column.key === "size_qty" ? (
                        <View style={styles.sizesContainer}>
                          {formatSize(row[column.key]).map(([size, quantity], sizeIndex) => (
                            <View key={sizeIndex} style={styles.tag}>
                              <Text>
                                {size}: {quantity}
                              </Text>
                            </View>
                          ))}
                        </View>
                      ) : (
                        <Text>
                          {column.key === "added_on" || column.key === "cleared_on"
                            ? formatDate(row[column.key])
                            : row[column.key]}
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              ))}
          </View>
        </Page>
      ))}
    </Document>
  );
};

const DailyReport = () => {
  const today = dayjs();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [form] = Form.useForm();
  const [dates, setDates] = useState([today, today]);
  const [filteredData, setFilteredData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [kanbanSearch, setKanbanSearch] = useState("");

  useEffect(() => {
    form.setFieldsValue({ dateRange: [today, today] });
    fetchData(today, today);
  }, []);

  const fetchData = async (fromdate, todate) => {
    try {
      const formData = new FormData();
      formData.append("from_date", fromdate.format("YYYY-MM-DD"));
      formData.append("to_date", todate.format("YYYY-MM-DD"));

      const response = await KCA_Service.getKCA_DailyReport(formData);

      setAllData(response?.data || []);
      setFilteredData(response?.data || []);

     
    } catch (error) {
      console.error("Fetch error:", error);
      setAllData([]);
      setFilteredData([]);
      ResponseMessage.error("Failed to fetch data");
    }
  };

  const handleSearch = () => {
    const values = form.getFieldsValue();

    if (!values.dateRange || !values.dateRange[0] || !values.dateRange[1]) {
      ResponseMessage.error("Please select a valid date range.");
      return;
    }

    const fromdate = values.dateRange[0].startOf("day");
    const todate = values.dateRange[1].endOf("day");

    setDates([fromdate, todate]);
    fetchData(fromdate, todate);
  };

  const handleKanbanSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setKanbanSearch(searchValue);

    const filtered = allData.filter((item) =>
      item.kanban_no?.toLowerCase().includes(searchValue)
    );
    setFilteredData(filtered);
  };

  const handleExportCSV = () => {
    if (!filteredData || filteredData.length === 0) {
      ResponseMessage.warning("No Data to Export");
      return;
    }

    const headerMap = {
      sno: "SI. No",
      gsm_m_id: "GSM ID",
      kanban_no: "Kanban No",
      size_qty: "Size & Qty",
      total_qty: "Total Qty",
      kanban_status: "Kanban Status",
      added_by: "Added By",
      added_on: "Added On",
      cleared_by: "Cleared By",
      cleared_on: "Cleared On",
    };

    const headers = Object.keys(headerMap);
    const csvHeaders = headers.map((key) => `"${headerMap[key]}"`).join(",");

    const csvRows = filteredData.map((item, index) => {
      return headers
        .map((header) => {
          let value;

          if (header === "sno") {
            value = index + 1;
          } else if (header === "size_qty") {
            try {
              const sizeObj = JSON.parse(item[header] || "{}");
              value = Object.entries(sizeObj)
                .map(([key, val]) => `${key}: ${val}`)
                .join(", ");
            } catch {
              value = item[header] || "";
            }
          } else if (header === "added_on" || header === "cleared_on") {
            value = item[header]
              ? new Date(item[header]).toISOString().replace("T", " ").substring(0, 19)
              : "";
          } else {
            value = item[header] || "";
          }

          return `"${value.toString().replace(/"/g, '""')}"`;
        })
        .join(",");
    });

    const csv = [csvHeaders, ...csvRows].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `daily_report_csv_${dates[0]?.format("YYYY-MM-DD")}_to_${dates[1]?.format(
      "YYYY-MM-DD"
    )}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    ResponseMessage.success("CSV Downloaded Successfully");
  };

  const handleExportPDF = async () => {
    if (!filteredData || filteredData.length === 0) {
      ResponseMessage.warning("No data to export");
      return;
    }

    try {
      const blob = await pdf(
        <DownloadPdf
          reportData={filteredData}
          startDate={dates[0]?.format("YYYY-MM-DD")}
          endDate={dates[1]?.format("YYYY-MM-DD")}
        />
      ).toBlob();

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `daily_report_${dates[0]?.format("YYYY-MM-DD")}_to_${dates[1]?.format(
        "YYYY-MM-DD"
      )}.pdf`;
      link.click();

      ResponseMessage.success("PDF Downloaded Successfully");
    } catch (error) {
      console.error("PDF Generation Error: ", error);
      ResponseMessage.error("Failed to generate PDF");
    }
  };

  const exportContent = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 140 }}>
      <Button type="text" onClick={handleExportCSV} style={{ textAlign: "left" }}>
        <strong>Export CSV</strong>
      </Button>
      <Button type="text" onClick={handleExportPDF} style={{ textAlign: "left" }}>
        <strong>Export PDF</strong>
      </Button>
    </div>
  );

  const columns = useMemo(
    () => [
      {
        title: "SI. No",
        key: "sno",
        render: (_, __, index) => index + 1,
        width: 80,
        fixed: isMobile ? false : "left",
      },
      { key: "gsm_m_id", title: "GSM ID", dataIndex: "gsm_m_id", width: 120 },
      { key: "kanban_no", title: "Kanban No", dataIndex: "kanban_no", width: 140 },
      {
        title: "Size & Qty",
        dataIndex: "size_qty",
        key: "size_qty",
        width: 220,
        render: (sizeQty) => {
          if (!sizeQty) return <Tag color="red">No Data</Tag>;

          if (typeof sizeQty === "string") {
            try {
              sizeQty = JSON.parse(sizeQty);
            } catch {
              return <Tag color="red">Invalid Data</Tag>;
            }
          }

          if (typeof sizeQty !== "object" || sizeQty === null) {
            return <Tag color="red">Invalid Format</Tag>;
          }

          return Object.entries(sizeQty).map(([size, qty]) => (
            <Tag
              key={size}
              color="blue"
              style={{
                marginBottom: 4,
                whiteSpace: "normal",
              }}
            >
              {size}: {qty}
            </Tag>
          ));
        },
      },
      { key: "total_qty", title: "Total Qty", dataIndex: "total_qty", width: 110 },
      {
        key: "kanban_status",
        title: "Kanban Status",
        dataIndex: "kanban_status",
        width: 140,
      },
      { key: "added_by", title: "Added By", dataIndex: "added_by", width: 120 },
      {
        key: "added_on",
        title: "Added On",
        dataIndex: "added_on",
        width: 180,
        render: (date) =>
          date ? new Date(date).toISOString().replace("T", " ").substring(0, 19) : "",
      },
      { key: "cleared_by", title: "Cleared By", dataIndex: "cleared_by", width: 120 },
      {
        key: "cleared_on",
        title: "Cleared On",
        dataIndex: "cleared_on",
        width: 180,
        render: (date) =>
          date ? new Date(date).toISOString().replace("T", " ").substring(0, 19) : "",
      },
    ],
    [isMobile]
  );

  const totalQuantity = filteredData.reduce(
    (sum, item) => sum + Number(item?.total_qty || 0),
    0
  );

  return (
    <>
      <Card className="modern-card dashboard-filter-wrap" style={{ marginBottom: 20 }}>
        <Row gutter={[16, 16]} align="top" justify="space-between">
          <Col xs={24} xl={8}>
            <div className="page-title-wrap">
              <AntText style={{ fontSize: 16, fontWeight: 600, color: "#1e293b" }}>
                Daily Report Filters
              </AntText>
              <AntText className="page-subtitle">
                Select a date range and search kanban details
              </AntText>
            </div>
          </Col>

          <Col xs={24} xl={16}>
            <Form layout="vertical" form={form}>
              <Row gutter={[12, 12]} align="bottom">
                <Col xs={24} md={10} lg={9}>
                  <Form.Item
                    label={<strong>Select Date Range</strong>}
                    name="dateRange"
                    style={{ marginBottom: 0 }}
                  >
                    <DatePicker.RangePicker
                      value={dates}
                      onChange={(dateRange) => {
                        setDates(dateRange || []);
                        form.setFieldsValue({ dateRange });
                      }}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8} lg={7}>
                  <Form.Item
                    label={<strong>Search Kanban No</strong>}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      placeholder="Enter Kanban Number"
                      value={kanbanSearch}
                      onChange={handleKanbanSearch}
                      prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={3} lg={4}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" onClick={handleSearch} block>
                      Get Result
                    </Button>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={3} lg={4}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Popover
                      placement={isMobile ? "bottom" : "bottomRight"}
                      content={exportContent}
                      trigger="click"
                      arrow={false}
                    >
                      <Button type="primary" icon={<DownloadOutlined />} block>
                        Export
                      </Button>
                    </Popover>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Card>

      <div className="compact-stats" style={{ marginBottom: 20 }}>
        <Row gutter={[16, 12]} justify="center">
          <Col xs={24} sm={8}>
            <div className="compact-stat-item">
              <div className="compact-stat-value" style={{ color: "#0f172a" }}>
                {filteredData.length}
              </div>
              <div className="compact-stat-label">Total Records</div>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div className="compact-stat-item">
              <div className="compact-stat-value" style={{ color: "#0f766e" }}>
                {totalQuantity}
              </div>
              <div className="compact-stat-label">Total Quantity</div>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div className="compact-stat-item">
              <div
                className="compact-stat-value"
                style={{
                  color: "#7c3aed",
                  fontSize: isMobile ? 14 : 16,
                  wordBreak: "break-word",
                }}
              >
                {dates?.[0]?.format("DD.MM.YYYY") || "--"} - {dates?.[1]?.format("DD.MM.YYYY") || "--"}
              </div>
              <div className="compact-stat-label">Selected Period</div>
            </div>
          </Col>
        </Row>
      </div>

      <Card className="modern-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            flexDirection: isMobile ? "column" : "row",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <AntText style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
              Daily Report Data
            </AntText>
            <div>
              <AntText className="page-subtitle">
                Review daily kanban count and accuracy records
              </AntText>
            </div>
          </div>

          <div
            style={{
              padding: "8px 14px",
              borderRadius: 12,
              background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
              border: "1px solid #e2e8f0",
              fontSize: 13,
              fontWeight: 600,
              color: "#334155",
              width: isMobile ? "100%" : "auto",
              textAlign: "center",
            }}
          >
            Records: {filteredData.length}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey={(record, index) => record.gsm_m_id || index}
          scroll={{ x: 1400 }}
          className="premium-report-table"
          size={isMobile ? "small" : "middle"}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: false,
            position: ["bottomRight"],
            responsive: true,
            showLessItems: true,
            itemRender: (page, type, originalElement) => {
              if (type === "prev") {
                return <span className="premium-page-arrow">‹</span>;
              }
              if (type === "next") {
                return <span className="premium-page-arrow">›</span>;
              }
              return originalElement;
            },
          }}
        />
      </Card>
    </>
  );
};

export default DailyReport;